import React, { useState } from 'react'
import {
  DataGrid,
  useGridApiRef
} from '@mui/x-data-grid'
import { styled } from '@mui/material/styles'
import notFound from "../../containers/Contacts/Table/NotFoundData"
import DataGridSkeletonOverlay from "../../components/Skeleton/DataGridSkeletonOverlay"
import { handleTableId } from "../../helpers/helper"
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal"
import { showSnackBar } from "../../components/Snackbar/Snackbar"
import { useMyContextLoading } from "../../components/LoadingContextProvider/LoadingContextProvider"
import { useDispatch } from "react-redux"
import { removeUser, resendUserInvite } from "../../features/users/users"
import { useNavigate } from "react-router-dom"
import UserStatusCell from "../Contacts/Table/Contact/StatusCell"
import { LuPencil, LuRotateCw, LuTrash2 } from "react-icons/lu"
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog"
import { useUiKit } from "../../components/UiKit/UiKitContext"
import { failureOf } from "../../utils/errorHelpers"


// Local styled component removed in favor of global theme overrides in linearTheme.js

const UsersTable = ({ data, setIsUpdated, isUpdated }) => {
  const apiRef = useGridApiRef()
  const [openDeleteTaskModal, setOpenDeleteTaskModal] = useState("")
  const { triggerLoading, isBarLoading } = useMyContextLoading()
  const { confirm: kitConfirm } = useUiKit()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const deleteUserOrThrow = async (userId) => {
    const response = await dispatch(removeUser(userId))
    if (!response?.payload?.data?._id) {
      throw failureOf(response, 'Something went wrong, please try again')
    }
    setIsUpdated(!isUpdated)
    showSnackBar("success", "User successfully deleted")
  }

  const deleteUsers = async (userId) => {
    triggerLoading(true)
    try {
      const response = await dispatch(removeUser(userId))
      if (response?.payload?.data?._id) {
        setIsUpdated(!isUpdated)
        showSnackBar("success", "User successfully deleted")
      } else {
        showSnackBar("error", "Something went wrong, please try again")
      }
    } catch (error) {
      console.error(error)
    }
    triggerLoading(false)
  }

  const handleResendInvite = async (userId) => {
    triggerLoading(true)
    try {
      const response = await dispatch(resendUserInvite(userId))
      if (response?.payload?.data?.success) {
        showSnackBar("success", "Invitation email has been resent")
      } else {
        showSnackBar("error", response?.payload || "Failed to resend invitation")
      }
    } catch (error) {
      console.error(error)
    }
    triggerLoading(false)
  }

  const columns = [

    {
      "field": "_id",
      "headerName": "User Id",
      renderCell: (params) => {
        return (
          <div className="text-primary-main font-semibold">
            {handleTableId(params.row?._id)}
          </div>
        )
      },
      sortable: false,
      // minWidth guard: flex alone is clamped to MUI's 50px floor when crowded; hold a full ObjectId
      flex: 1,
      minWidth: 200,
    },
    {
      "field": "name",
      "headerName": "Name",
      renderCell: (params) => {
        return (
          <div key={params?.row?.id} className="flex w-full">
            {params?.row?.firstName} {params?.row?.lastName}
          </div>
        )
      },
      sortable: false,
      // minWidth guard: flex slack-absorber, never below a full name
      flex: 1,
      minWidth: 160
    },
    {
      "field": "email",
      "headerName": "Email",
      sortable: false,
      // minWidth guard: flex slack-absorber; long addresses would clip without a floor
      flex: 1,
      minWidth: 220
    },
    {
      "field": "contactGroups",
      "headerName": "Department",
      renderCell: (params) => {
        return (
          <div key={params?.row?.id} className="flex w-full">
            {params?.row?.contactGroups?.[0]?.name || '—'}
          </div>
        )
      },
      sortable: false,
      // minWidth guard: clears the uppercase "DEPARTMENT" header + group name
      flex: 1,
      minWidth: 150
    },
    {
      "field": "subRole",
      "headerName": "Role",
      renderCell: (params) => {
        return (
          <div key={params?.row?.id} className="flex w-full">
            {params?.row?.subRole?.label || '-'}
          </div>
        )
      },
      sortable: false,
      // minWidth guard: clears the uppercase "ROLE" header + role label
      flex: 1,
      minWidth: 140
    },
    {
      "field": "status",
      "headerName": "Status",
      renderCell: (params) => {
        return (
          <div>
            {
              params?.row?.isPassword ?
                <UserStatusCell statusObj={{ bgColor: 'var(--color-success-background)', textColor: 'var(--color-success-text)', label: 'Active' }} />
                :
                <UserStatusCell statusObj={{ bgColor: 'var(--color-warning-background)', textColor: 'var(--color-warning-text)', label: 'Pending' }} />
            }
          </div>
        )
      },
      sortable: false,
      // minWidth guard: clears the uppercase "STATUS" header + status pill
      flex: 1,
      minWidth: 120
    },
    {
      "field": "action",
      "headerName": "Action",
      // fixed width (no flex): clears the uppercase "ACTION" header + up to 3 icon controls
      width: 150,
      headerAlign: 'right',
      align: 'right',
      renderCell: (params) => {
        const removeFunc = () => deleteUsers(params?.row?._id)
        return (
          <div key={params?.row?.id} className="flex items-center justify-end gap-3 w-full pr-1">
            {!params?.row?.isPassword && (
              <div className="cursor-pointer" title="Resend Invite" onClick={() => handleResendInvite(params?.row?._id)}>
                <LuRotateCw size={18} className="text-text-secondary" />
              </div>
            )}
            <div className="cursor-pointer" onClick={() => {
              navigate(`/users/edit/${params?.row?._id}`)
            }}>
              <LuPencil size={18} className="text-text-secondary" />
            </div>

            <div className="cursor-pointer" onClick={() => setOpenDeleteTaskModal(params?.row?._id)}>
              <LuTrash2 size={18} className="text-text-secondary hover:text-error-main" />
            </div>
            {openDeleteTaskModal === params?.row?._id && (kitConfirm ?
              <ConfirmDialog
                open={Boolean(openDeleteTaskModal)}
                onClose={() => setOpenDeleteTaskModal("")}
                title={`Delete ${[params?.row?.firstName, params?.row?.lastName].filter(Boolean).join(' ') || 'this user'}?`}
                impact={["The user account is removed from the user list.", "This cannot be undone."]}
                confirmLabel="Delete user"
                tone="danger"
                onConfirm={() => deleteUserOrThrow(params?.row?._id)}
              /> :
              <ConfirmModal leftTopIcon={<LuTrash2 size={18} className="text-text-secondary" />}
                title="Are you sure to delete the User?"
                modalButtonTitle={"Delete"} classes=""
                open={openDeleteTaskModal}
                setOpen={setOpenDeleteTaskModal}
                functionButton={removeFunc}
              >
                You will not be able to restore it later
              </ConfirmModal>)}
          </div>
        )
      },
      sortable: false
    }
  ]

  return (
    <div>
      <DataGrid
        sx={{
          border: 'none',
          '& .MuiDataGrid-columnHeaders': {
            // Keep the menu hiding if it was intentional
            '.MuiButtonBase-root[aria-haspopup="menu"]': {
              display: 'none'
            }
          },
          '.MuiDataGrid-virtualScroller': {
            minHeight: data?.length === 0 ? 300 : ''
          }
        }}
        rowHeight={75}
        columns={columns}
        slots={{
          noRowsOverlay: notFound,
          loadingOverlay: DataGridSkeletonOverlay,
        }}
        loading={isBarLoading && !(data?.length > 0)}
        columnVisibilityModel={{
          id: false
        }}
        rows={data}
        apiRef={apiRef}
        getRowId={(row) => row._id}
        hideFooterPagination={true}
        checkboxSelection={false}
        disableRowSelectionOnClick
        disableColumnSelector
        getRowClassName={(params) => params?.row?.status === 'Failed' ? 'bg-error-light' : ''}
      />

    </div>
  )
}

export default UsersTable
